import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
  useTheme,
  Paper,
  Container,
  Stack,
} from '@mui/material';
import { ShieldCheck, CreditCard, CheckCircle } from 'lucide-react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useNavigate } from 'react-router-dom';

interface PlanDetailsType {
  id: string;
  product?: {
    name?: string;
  };
  unit_amount?: number;
  recurring?: {
    interval?: string;
  };
}

interface AddonItem {
  id: string;
  product?: {
    name?: string;
  };
  unit_amount?: number;
  quantity: number;
}

interface PaymentFormProps {
  selectedPlanDetails: PlanDetailsType;
  selectedAddons: AddonItem[];
  handlePaymentSubmit: (
    e: React.FormEvent<HTMLFormElement>,
    paymentMethodId?: string,
    billingDetails?: any
  ) => void;
  isCurrentPlan?: boolean;
  isTrialActive?: boolean;
  isSubmitting?: boolean;
  isEditing?: boolean;
  customerCardDetails?: {
    id?: string;
    brand?: string;
    last4?: string;
    exp_month?: number;
    exp_year?: number;
    billing_email?: string;
    name?: string;
  };
}

const formatCurrency = (amount: number) => `$${(amount / 100).toFixed(2)}`;

const PaymentForm: React.FC<PaymentFormProps> = ({
  selectedPlanDetails,
  selectedAddons,
  handlePaymentSubmit,
  isCurrentPlan = false,
  isTrialActive = false,
  isSubmitting = false,
  isEditing = false,
  customerCardDetails,
}) => {
  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );

  const cardElementOptions = useMemo(
    () => ({
      style: {
        base: {
          fontSize: '16px',
          color: theme.palette.text.primary,
          fontFamily: "'Inter', sans-serif",
          fontSmoothing: 'antialiased',
          '::placeholder': {
            color: theme.palette.text.secondary,
          },
        },
        invalid: {
          color: theme.palette.error.main,
        },
      },
      hidePostalCode: true,
    }),
    [theme]
  );

  const getButtonText = useMemo(() => {
    if (isEditing) return 'Update Payment Method';
    if (isCurrentPlan && !isTrialActive) return 'Manage Plan';
    if (isTrialActive) return 'Start Subscription';
    return 'Confirm Payment';
  }, [isEditing, isCurrentPlan, isTrialActive]);

  useEffect(() => {
    if (isEditing && customerCardDetails?.billing_email) {
      setEmail(customerCardDetails.billing_email);
      setFirstName(customerCardDetails.name?.split(' ')[0] || '');
      setLastName(customerCardDetails.name?.split(' ')[1] || '');
    }
  }, [isEditing, customerCardDetails]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setPaymentError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');

      const billingDetails = {
        name: `${selectedOrganization?.client.firstName || firstName} ${selectedOrganization?.client.lastName || lastName}`.trim(),
        email: email || selectedOrganization?.client.email,
      };

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: billingDetails,
      });

      if (error) {
        setPaymentError(error.message || 'Payment error occurred');
        return;
      }

      if (!paymentMethod?.id) throw new Error('Payment method creation failed');

      await handlePaymentSubmit(e, paymentMethod.id, billingDetails);
      setPaymentSuccess(true);
    } catch (err: any) {
      setPaymentError(err.message || 'An unexpected error occurred');
      setPaymentSuccess(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const pageTitle = useMemo(() => {
    if (isEditing) return 'Update Payment Method';
    return isTrialActive ? 'Complete Your Subscription' : 'Checkout';
  }, [isEditing, isTrialActive]);

  if (paymentSuccess) {
    return (
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 1,
            textAlign: 'center',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              mx: 'auto',
            }}
          >
            <CheckCircle size={32} color={theme.palette.success.main} />
          </Box>
          <Typography variant="h6" gutterBottom fontWeight="500">
            Payment Successful
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Your subscription has been activated.
          </Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Typography
        variant="h5"
        component="h1"
        gutterBottom
        sx={{ fontWeight: 500, mb: 4, textAlign: 'center' }}
      >
        {pageTitle}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={4}>
          {/* Payment Method Section */}
          <Paper
            elevation={0}
            sx={{ borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Payment Method
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 2,
                  mb: 4,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CreditCard
                    size={18}
                    style={{ marginRight: 8, color: theme.palette.text.secondary }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    Credit or Debit Card
                  </Typography>
                </Box>
                <CardElement options={cardElementOptions} />
              </Box>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Billing Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="First Name"
                    fullWidth
                    size="small"
                    value={firstName}
                    defaultValue={selectedOrganization?.client.firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Last Name"
                    fullWidth
                    size="small"
                    value={lastName}
                    defaultValue={selectedOrganization?.client.lastName}
                    onChange={e => setLastName(e.target.value)}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Email Address"
                    fullWidth
                    size="small"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    defaultValue={selectedOrganization?.client.email}
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', mb: 3, alignItems: 'flex-start' }}>
                <ShieldCheck
                  size={16}
                  style={{ marginRight: 8, marginTop: '2px', color: theme.palette.text.secondary }}
                />
                <Typography variant="caption" color="textSecondary">
                  Your payment information is processed securely by Stripe. We do not store your
                  credit card details.
                </Typography>
              </Box>

              {paymentError && (
                <Typography variant="body2" sx={{ color: theme.palette.error.main, mb: 2 }}>
                  {paymentError}
                </Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={
                  !stripe || isProcessing || (isCurrentPlan && !isTrialActive && !isEditing)
                }
                sx={{ py: 1.2 }}
              >
                {isProcessing ? 'Processing...' : getButtonText}
              </Button>
            </Box>
          </Paper>
        </Stack>
      </form>
    </Container>
  );
};

export default PaymentForm;
